import { releasedStarters, starterMoveCost, type GameState, type StarterBiome } from '@thicket/core';
import { html, nothing } from 'lit-html';
import { art, icon } from '../garden/art/index.js';
import { labPreview, labReady } from '../garden/art/lab/index.js';
import { app, BIOME_NAMES, toast, update } from './app.js';
import { maybeStartTour } from './tour.js';

/** What each starting place is like: its look, its four native trees (trivial to hard) and where habits grow. */
const ABOUT: Record<StarterBiome, { blurb: string; trees: string[]; bed: string }> = {
  meadow: { blurb: 'Open grass, a brook and a stone bed. The roomiest place to grow a forest.', trees: ['Birch', 'Maple', 'Oak', 'Spruce'], bed: 'Stone raised bed, 18 plots' },
  pineRidge: { blurb: 'Cool air, a river and rocky ledges. Firs and chestnuts on the high ground.', trees: ['Aspen', 'Rowan', 'Chestnut', 'Fraser fir'], bed: 'Flower cart, 6 plots' },
  canyon: { blurb: 'Red rock and mesas. Saguaros and palo verde in the sun.', trees: ['Palo verde', 'Desert willow', 'Mesquite', 'Saguaro'], bed: 'Flower cart, 6 plots' },
  wetland: { blurb: 'Boardwalks over still water, cypress and willow.', trees: ['Black willow', 'Swamp maple', 'Tupelo', 'Bald cypress'], bed: 'Flower cart, 6 plots' },
  jungleRuins: { blurb: 'Vines over old stone, flame trees and figs.', trees: ['Cecropia', 'Flame tree', 'Strangler fig', 'Kapok'], bed: 'Flower cart, 6 plots' },
};
const SPECIES = ['birch', 'maple', 'oak', 'pine'] as const;
/** The order the places are offered in. */
const ORDER: StarterBiome[] = ['meadow', 'pineRidge', 'canyon', 'wetland', 'jungleRuins'];
const offered = () => ORDER.filter((b) => releasedStarters(app.store.rules).includes(b));

const treeIcon = (biome: StarterBiome, species: (typeof SPECIES)[number]) =>
  icon(`starter-tree:${biome}:${species}`, (ctx) => art.tree(ctx, { x: 16, y: 31, species, stage: 'large', sparse: false, biome, seed: 3, tick: 0, reduce: true }), 32, 32);

/** True while a new player still has to pick where the garden begins. */
export const choosingStarter = (s: GameState) =>
  !s.profile.starterChosen && !s.plants.some((p) => !p.goneAt && p.biome !== null) && !s.inventory.some((i) => i.type === 'animal');

/** One starting place, with its preview, trees and bed, as a pressable card. */
export function starterCard(s: GameState, b: StarterBiome, pressed: boolean, onPick: () => void, note?: unknown) {
  const ready = labReady();
  const src = ready ? labPreview(b, s) : null;
  const a = ABOUT[b];
  return html`<button class="starter-card" aria-pressed=${pressed} @click=${onPick}>
    ${src ? html`<img src=${src} alt=${`The ${BIOME_NAMES[b].toLowerCase()}`}>` : html`<span class="starter-blank">Growing…</span>`}
    <span class="starter-body">
      <span class="starter-name">${BIOME_NAMES[b]}${note ?? nothing}</span>
      <span class="small muted">${a.blurb}</span>
      <span class="starter-trees">${a.trees.map((name, i) => html`<span><img src=${ready ? treeIcon(b, SPECIES[i]!) : ''} alt="" width="24" height="24">${name}</span>`)}</span>
      <span class="small muted">Habits grow in: ${a.bed}</span>
    </span>
  </button>`;
}

let picked: StarterBiome | null = null;

/** First open: pick where the garden begins. Shown until the player chooses (or plants something). */
export function starterView(s: GameState) {
  const starters = offered();
  const choice = picked ?? starters[0] ?? 'meadow';
  return html`<main class="wrap starter">
    <h2>Where does your garden begin?</h2>
    <p class="muted">Your dailies grow into this place's trees. You can move later from Settings, but your trees start over when you do.</p>
    <div class="starter-grid">${starters.map((b) => starterCard(s, b, choice === b, () => update(() => (picked = b))))}</div>
    <button class="primary" @click=${() => { if (app.store.act({ type: 'chooseStarter', biome: choice })) maybeStartTour(); }}>Start in the ${BIOME_NAMES[choice].toLowerCase()}</button>
  </main>`;
}

let moveTo: StarterBiome | null = null;

/** Settings: change the starting place, with what it costs spelled out before the second tap. */
export function starterSettings(s: GameState) {
  const starters = offered();
  const current = s.profile.starterBiome;
  const cost = starterMoveCost(s);
  const target = moveTo && moveTo !== current ? moveTo : null;
  const lost: string[] = [];
  if (cost.trees) lost.push(`${cost.trees} tree${cost.trees === 1 ? '' : 's'} (your dailies start over as seeds, streaks included)`);
  if (cost.wildflowers) lost.push(`${cost.wildflowers} wildflower${cost.wildflowers === 1 ? '' : 's'} (open to-dos get a fresh bud)`);
  if (cost.flowers) lost.push(`${cost.flowers} planted flower${cost.flowers === 1 ? '' : 's'}`);
  if (cost.companions) lost.push(`${cost.companions} tree companion${cost.companions === 1 ? '' : 's'}`);
  const move = () => {
    if (!target) return;
    const ok = app.store.act({ type: 'chooseStarter', biome: target, confirm: true });
    if (ok) {
      toast(`Moved to the ${BIOME_NAMES[target].toLowerCase()}`, 3500);
      moveTo = null;
      app.biome = null;
    }
  };
  return html`<section class="box form" id="starting-place">
    <h2>Starting place</h2>
    <p style="margin:0">Your garden is in the <b>${BIOME_NAMES[current].toLowerCase()}</b>. The other places open as you level; until then you can move once you like, and your bed, its plants and its companions come along.</p>
    <div class="starter-grid">${starters.map((b) => starterCard(s, b, (target ?? current) === b, () => update(() => (moveTo = b)),
      b === current ? html`<span class="pip" style="margin-left:8px">Here now</span>` : nothing))}</div>
    ${target ? html`<div class="box banner warn">
        <p style="margin:0 0 6px"><b>Moving to the ${BIOME_NAMES[target].toLowerCase()} leaves the ${BIOME_NAMES[current].toLowerCase()} behind.</b></p>
        ${lost.length ? html`<p style="margin:0 0 6px">You lose:</p><ul style="margin:0 0 8px;padding-left:20px">${lost.map((l) => html`<li>${l}</li>`)}</ul>` : html`<p style="margin:0 0 8px">Nothing is growing there yet, so nothing is lost.</p>`}
        <p class="small muted" style="margin:0 0 10px">Coins, XP, level and your tasks all stay. There's no undo.</p>
        <div class="actions"><button class="primary" @click=${move}>Move to the ${BIOME_NAMES[target].toLowerCase()}</button>
          <button @click=${() => update(() => (moveTo = null))}>Keep the ${BIOME_NAMES[current].toLowerCase()}</button></div>
      </div>` : nothing}
  </section>`;
}
